import { types, Instance } from "mobx-state-tree";
import { StringModel } from "../../mobxDataModels/stringModel";
import { Mk8sDraftAutoscalerModel } from "./mk8s.draft.providerAutoscaler";
import { Mk8sDraftNetworkingModel } from "./mk8s.draft.providerNetworking";
import { ListOfItemsModel } from "../../mobxDataModels/listOfItemsModel";
import {
  Mk8sDraftProviderAzureAMIModel,
  Mk8sDraftProviderAzureAMIReadOnlyModel,
} from "./mk8s.draft.provider.azure.ami";
import {
  Mk8sDraftProviderAzureNodePoolsModel,
  Mk8sDraftProviderAzureNodePoolsReadonlyModel,
} from "./mk8s.draft.provider.azure.nodePool";
import { ngParseLink } from "../../utils/linkParser/linkParser";

export const Mk8sDraftProviderAzureModel = types
  .model({
    _location: types.optional(types.string, ""),
    location: types.optional(StringModel, () =>
      StringModel.create({
        label: "Location",
        isRequired: true,
      }),
    ),
    _subscriptionId: types.optional(types.string, ""),
    subscriptionId: types.optional(StringModel, () =>
      StringModel.create({ label: "Subscription ID", isRequired: true }),
    ),
    _sdkSecretLink: types.optional(types.string, ""),
    sdkSecretName: types.optional(types.string, ""),
    _resourceGroup: types.optional(types.string, ""),
    resourceGroup: types.optional(StringModel, () => StringModel.create({ label: "Resource Group", isRequired: true })),
    networking: types.optional(Mk8sDraftNetworkingModel, () => Mk8sDraftNetworkingModel.create()),
    _preInstallScript: types.optional(types.string, ""),
    preInstallScript: types.optional(StringModel, () => StringModel.create({ label: "Pre Install Script" })),
    _image: types.optional(Mk8sDraftProviderAzureAMIReadOnlyModel, () =>
      Mk8sDraftProviderAzureAMIReadOnlyModel.create(),
    ),
    image: types.optional(Mk8sDraftProviderAzureAMIModel, () => Mk8sDraftProviderAzureAMIModel.create()),
    _networkId: types.optional(types.string, ""),
    networkId: types.optional(StringModel, () => StringModel.create({ label: "Network ID", isRequired: true })),
    sshKeys: types.optional(ListOfItemsModel, () => ListOfItemsModel.create()),
    _tags: types.optional(types.frozen(), {}),
    tags: types.optional(ListOfItemsModel, () => ListOfItemsModel.create()),
    _nodePools: types.array(Mk8sDraftProviderAzureNodePoolsReadonlyModel),
    nodePools: types.array(Mk8sDraftProviderAzureNodePoolsModel),
    autoscaler: types.optional(Mk8sDraftAutoscalerModel, () => Mk8sDraftAutoscalerModel.create()),
    removed: types.optional(types.boolean, false),
  })
  .views((self) => ({
    get _sdkSecretName() {
      const { name: sdkSecretName } = ngParseLink(self._sdkSecretLink, { kind: "secret" });
      return sdkSecretName;
    },
    get sdkSecretLink() {
      const { absolute } = ngParseLink(self.sdkSecretName, { kind: "secret" });
      return absolute;
    },
    get tagsAsObject() {
      let res: any = {};
      for (let item of self.tags.items) {
        res[item.firstValue] = item.secondValue;
      }
      return res;
    },
  }))
  .actions((self) => ({
    reset() {
      self.location.setInitialValue(self._location);
      self.subscriptionId.setInitialValue(self._subscriptionId);
      self.sdkSecretName = self._sdkSecretName;
      self.resourceGroup.setInitialValue(self._resourceGroup);
      self.networking.reset();
      self.preInstallScript.setInitialValue(self._preInstallScript);
      const imageType = !!self._image.recommended ? "recommended" : "reference";
      self.image = Mk8sDraftProviderAzureAMIModel.create({
        recommended: self._image.recommended,
        reference_publisher: StringModel.create({
          label: "Publisher",
          initialValue: self._image.reference?.publisher,
          isRequired: true,
        }),
        reference_offer: StringModel.create({
          label: "Offer",
          initialValue: self._image.reference?.offer,
          isRequired: true,
        }),
        reference_sku: StringModel.create({ label: "SKU", initialValue: self._image.reference?.sku, isRequired: true }),
        reference_version: StringModel.create({
          label: "Version",
          initialValue: self._image.reference?.version,
          isRequired: true,
        }),
        type: imageType,
      });
      self.networkId.setInitialValue(self._networkId);
      self.sshKeys.reset();
      self.tags = ListOfItemsModel.create({
        _items: Object.entries(self._tags).map((entry) => ({
          firstValue: entry[0],
          secondValue: entry[1] as string,
        })),
      });
      self.nodePools.clear();
      for (let index in self._nodePools) {
        const _nodePool = self._nodePools[index];

        self.nodePools.push(
          Mk8sDraftProviderAzureNodePoolsModel.create({
            index: Number(index),
            _pool: JSON.parse(JSON.stringify(_nodePool)),
          }),
        );
      }
      self.autoscaler.reset();
      self.removed = false;
    },
    confirm() {
      self.location.confirm();
      self._location = self.location.value;
      self.subscriptionId.confirm();
      self._subscriptionId = self.subscriptionId.value;
      self._sdkSecretLink = self.sdkSecretLink;
      self.resourceGroup.confirm();
      self._resourceGroup = self.resourceGroup.value;
      self.networking.confirm();
      self.preInstallScript.confirm();
      self._preInstallScript = self.preInstallScript.value;
      self._image = {
        recommended: self.image.recommended,
        reference:
          self.image.type === "reference"
            ? {
                publisher: self.image.reference_publisher.value,
                offer: self.image.reference_offer.value,
                sku: self.image.reference_sku.value,
                version: self.image.reference_version.value,
              }
            : undefined,
      };
      self.networkId.confirm();
      self._networkId = self.networkId.value;
      self.sshKeys.confirm();
      self._tags = self.tagsAsObject;
      self.tags.confirm();
      self._nodePools.clear();
      for (let nodePool of self.nodePools) {
        nodePool.confirm();
        self._nodePools.push(
          Mk8sDraftProviderAzureNodePoolsReadonlyModel.create({
            name: nodePool.name.value, //
            labels: nodePool.labels.items.map((l) => ({ key: l.firstValue, value: l.secondValue })),
            taints: nodePool.taints.items.map((t) => ({
              key: t.firstValue,
              value: t.secondValue,
              effect: t.thirdValue,
            })),
            size: nodePool.size.value,
            subnetId: nodePool.subnetId.value,
            zones: nodePool.zones.items.map((i) => Number(i.firstValue)),
            minSize: String(nodePool.minSize.value).length > 0 ? Number(nodePool.minSize.value) : undefined,
            maxSize: String(nodePool.maxSize.value).length > 0 ? Number(nodePool.maxSize.value) : undefined,
            overrideImage: {
              recommended: nodePool.overrideImage.recommended,
              reference:
                nodePool.overrideImage.type === "reference"
                  ? {
                      publisher: nodePool.overrideImage.reference_publisher.value,
                      offer: nodePool.overrideImage.reference_offer.value,
                      sku: nodePool.overrideImage.reference_sku.value,
                      version: nodePool.overrideImage.reference_version.value,
                    }
                  : undefined,
            },
            bootDiskSize: Number(nodePool.bootDiskSize.value),
          }),
        );
      }
      self.autoscaler.confirm();
      self.removed = false;
    },
  }))
  .actions((self) => ({
    afterCreate() {
      self.reset();
    },
    setSDKSecretName(value: string) {
      self.sdkSecretName = value;
    },
    addNodePool() {
      let index = 0;
      if (self.nodePools.length > 0) {
        index = self.nodePools[self.nodePools.length - 1].index + 1;
      }
      self.nodePools.push(
        Mk8sDraftProviderAzureNodePoolsModel.create({
          index,
          status: "added",
        }),
      );
    },
    removeNodePoolAt(index: number) {
      const node = self.nodePools.find((np) => np.index === index);
      if (!node) {
        return;
      }
      if (node.status === "default") {
        self.removed = true;
      }
      self.nodePools.remove(node);
    },
  }))
  .views((self) => ({
    isNodePoolNameValid(index: number) {
      let res = true;
      const node = self.nodePools.find((np) => np.index === index)!;
      const pools = [...self.nodePools.filter((np) => np.index !== index).slice()];
      if (pools.some((np) => np.name.value === node.name.value)) {
        res = false;
      }
      return res;
    },
    get anyNodePoolNameConflicts() {
      for (const nodePool of self.nodePools) {
        if (
          [...self.nodePools.filter((np) => np.index !== nodePool.index).slice()].some(
            (np) => np.name.value === nodePool.name.value,
          )
        ) {
          return true;
        }
      }
      return false;
    },
  }))
  .views((self) => ({
    get isDirty() {
      let res = false;
      if (self.location.isDirty) res = true;
      if (self.subscriptionId.isDirty) res = true;
      if (self.sdkSecretName !== self._sdkSecretName) res = true;
      if (self.resourceGroup.isDirty) res = true;
      if (self.networking.isDirty) res = true;
      if (self.preInstallScript.isDirty) res = true;
      const initialImageType = !!self._image.recommended ? "recommended" : "reference";
      if (initialImageType !== self.image.type) res = true;
      if (self.image.type === "reference") {
        if (self.image.reference_publisher.value !== self._image.reference?.publisher) res = true;
        if (self.image.reference_offer.value !== self._image.reference?.offer) res = true;
        if (self.image.reference_sku.value !== self._image.reference?.sku) res = true;
        if (self.image.reference_version.value !== self._image.reference?.version) res = true;
      } else if (self.image.type === "recommended") {
        if (self.image.recommended !== self._image.recommended) res = true;
      }
      if (self.networkId.isDirty) res = true;
      if (self.sshKeys.isDirty) res = true;
      if (self.tags.isDirty) res = true;
      if (self._nodePools.length !== self.nodePools.length) res = true;
      if (self.removed) res = true;
      if (self.nodePools.some((n) => n.isDirty)) res = true;
      if (self.autoscaler.isDirty) res = true;
      return res;
    },
    get dirtyReason() {
      let reason = "";
      return reason;
    },
    get isValid() {
      let res = true;
      if (!self.location.isValid) res = false;
      // azure tags
      if (!self.subscriptionId.isValid) res = false;
      // sdk secret link
      if (!self.resourceGroup.isValid) res = false;
      if (!self.networking.isValid) res = false;
      if (!self.preInstallScript.isValid) res = false;
      if (self.image.type === "recommended") {
        if (!self.image.recommended) res = false;
      } else {
        if (
          !self.image.reference_offer.value ||
          !self.image.reference_publisher.value ||
          !self.image.reference_sku.value ||
          !self.image.reference_version.value
        )
          res = false;
      }
      if (!self.networkId.isValid) res = false;
      if (self.sshKeys.items.length < 1) res = false;
      // tags
      if (self.anyNodePoolNameConflicts) res = false;
      if (self.nodePools.some((n) => !n.isValid)) res = false;
      if (!self.autoscaler.isValid) res = false;
      return res;
    },
    get invalidReason() {
      let reason = "";
      return reason;
    },
    get ui_isAzureValid() {
      let res = true;
      if (!self.location.isValid) res = false;
      if (!self.subscriptionId.isValid) res = false;
      if (!self.resourceGroup.isValid) res = false;
      if (self.image.type === "recommended") {
        if (!self.image.recommended) res = false;
      } else {
        if (
          !self.image.reference_offer.value ||
          !self.image.reference_publisher.value ||
          !self.image.reference_sku.value ||
          !self.image.reference_version.value
        )
          res = false;
      }
      if (!self.networkId.isValid) res = false;
      if (self.sshKeys.items.length < 1) res = false;
      return res;
    },
    get dryRun_azurePathList() {
      return [
        //
        "spec.provider.azure.location",
        "spec.provider.azure.subscriptionId",
        "spec.provider.azure.resourceGroup",
        "spec.provider.azure.image",
        "spec.provider.azure.image.recommended",
        "spec.provider.azure.image.reference",
        "spec.provider.azure.image.reference.publisher",
        "spec.provider.azure.image.reference.offer",
        "spec.provider.azure.image.reference.sku",
        "spec.provider.azure.image.reference.version",
        "spec.provider.azure.networkId",
        "spec.provider.azure.sshKeys",
        ...self.sshKeys.items.map((_, index) => `spec.provider.azure.sshKeys[${index}]`),
      ];
    },
    get ui_isAdvancedValid() {
      let res = true;
      // azuretags
      // tags
      if (!self.networking.isValid) res = false;
      if (!self.preInstallScript.isValid) res = false;
      if (!self.autoscaler.isValid) res = false;
      return res;
    },
    get dryRun_advancedPathList() {
      return [
        //
        "spec.provider.azure.azureTags",
        "spec.provider.azure.tags",
        ...self.networking.dryRun_pathList("azure"),
        "spec.provider.azure.preInstallScript",
        ...self.autoscaler.dryRun_pathList("azure"),
      ];
    },
    get asObject() {
      let obj: any = {
        location: self.location.value,
        subscriptionId: self.subscriptionId.value,
        sdkSecretLink: self.sdkSecretLink,
        resourceGroup: self.resourceGroup.value,
        networking: self.networking.asObject,
        preInstallScript: self.preInstallScript.value,
        networkId: self.networkId.value,
        sshKeys: self.sshKeys.items.map((i) => i.firstValue),
        tags: self.tagsAsObject,
        autoscaler: self.autoscaler.asObject,
        nodePools: self.nodePools.map((n) => n.asObject),
      };

      if (self.image.type === "reference") {
        obj.image = {
          reference: {
            publisher: self.image.reference_publisher.value,
            offer: self.image.reference_offer.value,
            sku: self.image.reference_sku.value,
            version: self.image.reference_version.value,
          },
        };
      } else if (self.image.type === "recommended") {
        obj.image = { recommended: self.image.recommended };
      }
      return obj;
    },
  }));
export interface Mk8sDraftProviderAzureMobx extends Instance<typeof Mk8sDraftProviderAzureModel> {}
